// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { courseAPI, vmAPI, vmColumnsAPI, workspaceAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Monitor,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronRight,
  Search,
  Server,
  Eye,
  EyeOff,
  Copy,
  Check,
  X,
  ChevronsUpDown,
  Pencil,
  GripVertical,
  FileCode,
} from "lucide-react";
import { toast } from "sonner";
import { getInitials } from "@/lib/utils";

// Default columns removed - now loaded dynamically from API

// Editable column header component
function EditableColumnHeader({ column, canEdit, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(column.label);
  const inputRef = useRef(null);

  useEffect(() => { setLocalVal(column.label); }, [column.label]);
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

  const handleSave = () => {
    setEditing(false);
    if (localVal.trim() && localVal !== column.label) {
      onRename(column.id, localVal.trim());
    } else {
      setLocalVal(column.label);
    }
  };

  if (editing && canEdit) {
    return (
      <input
        ref={inputRef}
        value={localVal}
        onChange={(e) => setLocalVal(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setLocalVal(column.label); setEditing(false); } }}
        className="w-full bg-transparent border-0 border-b border-primary/50 outline-none text-xs font-semibold uppercase tracking-wider py-0.5 text-foreground"
      />
    );
  }

  return (
    <div className="flex items-center gap-1 group/header">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">
        {column.label}
      </span>
      {canEdit && (
        <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
          className="opacity-0 group-hover/header:opacity-100 p-0.5 rounded hover:bg-muted transition-opacity" title="Renommer">
          <Pencil className="h-2.5 w-2.5 text-muted-foreground" />
        </button>
      )}
      {canEdit && !column.is_default && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(column.id); }}
          className="opacity-0 group-hover/header:opacity-100 p-0.5 rounded hover:bg-destructive/10 transition-opacity" title="Supprimer la colonne">
          <X className="h-2.5 w-2.5 text-destructive/60" />
        </button>
      )}
    </div>
  );
}

// Inline editable cell component
function EditableCell({ value, onBlur, placeholder, isPassword = false }) {
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value || "");
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalVal(value || "");
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleBlur = () => {
    setEditing(false);
    if (localVal !== (value || "")) {
      onBlur(localVal);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
    if (e.key === "Escape") { setLocalVal(value || ""); setEditing(false); }
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={localVal}
        onChange={(e) => setLocalVal(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full bg-transparent border-0 border-b-2 border-primary/50 outline-none text-sm py-1 px-1 text-foreground placeholder:text-muted-foreground/40 focus:border-primary transition-colors"
      />
    );
  }

  if (isPassword && value) {
    return (
      <div className="flex items-center gap-1 group cursor-pointer" onClick={() => setEditing(true)}>
        <span className="text-sm truncate flex-1 min-w-0">
          {showPassword ? value : "••••••••"}
        </span>
        <button onClick={(e) => { e.stopPropagation(); setShowPassword(!showPassword); }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted">
          {showPassword ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
        </button>
        <button onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted">
          {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3 text-muted-foreground" />}
        </button>
      </div>
    );
  }

  return (
    <div className="text-sm py-1 px-1 cursor-pointer rounded hover:bg-muted/50 transition-colors min-h-[28px] flex items-center"
      onClick={() => setEditing(true)}>
      {value ? (
        <span className="truncate">{value}</span>
      ) : (
        <span className="text-muted-foreground/40 italic text-xs">{placeholder}</span>
      )}
    </div>
  );
}

// Inline participant select dropdown for new row
function InlineParticipantSelect({ participants, existingIds, onSelect, onCancel }) {
  const [open, setOpen] = useState(true);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onCancel();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onCancel]);

  const available = participants.filter(
    (p) =>
      !existingIds.includes(p.id) &&
      `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative w-full">
      {/* Select trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm border border-primary/40 rounded-lg bg-background hover:border-primary/60 transition-colors"
      >
        <span className="text-muted-foreground/60 text-xs">Sélectionner...</span>
        <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 w-72 bg-popover border border-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
          {/* Search */}
          <div className="p-2 border-b border-border/50">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 text-xs bg-muted/50 border border-border/50 rounded-md outline-none focus:ring-1 focus:ring-primary/30 transition-all placeholder:text-muted-foreground/50"
                autoFocus
              />
            </div>
          </div>
          {/* List */}
          <div className="max-h-44 overflow-y-auto">
            {available.length === 0 ? (
              <div className="flex flex-col items-center py-5 text-muted-foreground/50">
                <p className="text-xs">{search ? "Aucun résultat" : "Tous les participants sont déjà ajoutés"}</p>
              </div>
            ) : (
              available.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setOpen(false); onSelect(p); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-primary/5 transition-colors duration-100"
                >
                  <Avatar className="h-6 w-6 ring-1 ring-border/30">
                    <AvatarFallback className="bg-gradient-to-br from-violet-500/20 to-purple-600/20 text-primary text-[9px] font-semibold">
                      {getInitials(p.first_name, p.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{p.first_name} {p.last_name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{p.email}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Course VM Table component
function CourseVMTable({ course, entries, participants, vmColumns, onAddEntry, onUpdateEntry, onDeleteEntry, onRenameColumn, onDeleteColumn, onAddColumn, canEdit }) {
  const [isOpen, setIsOpen] = useState(true);
  const [addingRow, setAddingRow] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingIds, setDeletingIds] = useState(new Set());

  const existingParticipantIds = entries.map((e) => e.participant_id);

  const getParticipant = (participantId) => {
    return participants.find((p) => p.id === participantId);
  };

  const handleSelectParticipant = async (participant) => {
    setSavingId(participant.id);
    try {
      await onAddEntry(course.id, participant.id);
      setAddingRow(false);
    } catch (e) {
      // error handled in parent
    } finally {
      setSavingId(null);
    }
  };

  // Helper to get cell value for a column (default vs custom)
  const getCellValue = (entry, col) => {
    if (col.is_default) return entry[col.key] || "";
    return (entry.custom_fields || {})[col.key] || "";
  };

  const handleCellUpdate = async (entryId, col, value) => {
    if (col.is_default) {
      await onUpdateEntry(entryId, { [col.key]: value });
    } else {
      await onUpdateEntry(entryId, { custom_fields: { [col.key]: value } });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === entries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(entries.map((e) => e.id)));
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    setDeletingIds(new Set(ids));
    for (const id of ids) {
      try {
        await onDeleteEntry(id);
      } catch (e) {
        // error handled in parent
      }
    }
    setSelectedIds(new Set());
    setDeletingIds(new Set());
  };

  const handleSingleDelete = async (id) => {
    setDeletingIds(new Set([id]));
    try {
      await onDeleteEntry(id);
    } catch (e) {
      // error handled in parent
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setDeletingIds(new Set());
  };

  const allSelected = entries.length > 0 && selectedIds.size === entries.length;
  const someSelected = selectedIds.size > 0;

  // Compute column widths dynamically
  const colCount = vmColumns.length;
  const colWidth = colCount <= 3 ? '1fr' : '1fr';
  const gridCols = canEdit
    ? `36px 1fr ${vmColumns.map(() => colWidth).join(" ")} 40px`
    : `1fr ${vmColumns.map(() => colWidth).join(" ")}`;

  return (
    <div className={`rounded-xl border border-border/60 bg-card/50 backdrop-blur-sm shadow-sm hover:shadow-md transition-shadow duration-300 relative ${addingRow ? 'z-30' : 'z-0'}`}>
      {/* Course Header */}
      <div
        className="flex items-center gap-3 px-5 py-3.5 cursor-pointer select-none bg-gradient-to-r from-muted/30 to-transparent hover:from-muted/50 transition-all duration-200"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
          {isOpen ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4 text-primary" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm truncate">{course.title}</h3>
          {course.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{course.description}</p>}
        </div>
        {/* Bulk delete button */}
        {canEdit && someSelected && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive/60"
            onClick={(e) => { e.stopPropagation(); handleBulkDelete(); }}
            disabled={deletingIds.size > 0}
          >
            {deletingIds.size > 0 ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
            Supprimer ({selectedIds.size})
          </Button>
        )}
        <Badge variant="secondary" className="text-xs font-medium tabular-nums">
          {entries.length} VM{entries.length !== 1 ? "s" : ""}
        </Badge>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50"
            onClick={(e) => { e.stopPropagation(); setIsOpen(true); setAddingRow(true); }}
          >
            <Plus className="h-3 w-3" />
            Ajouter
          </Button>
        )}
      </div>

      {/* Table Content */}
      {isOpen && (
        <div className="border-t border-border/40">
          {/* Table Header */}
          <div className="grid gap-0 bg-muted/30 border-b border-border/40" style={{ gridTemplateColumns: gridCols }}>
            {canEdit && (
              <div className="px-2 py-2.5 flex items-center justify-center border-r border-border/20">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => { e.stopPropagation(); toggleSelectAll(); }}
                  className="h-3.5 w-3.5 rounded border-border/50 text-primary focus:ring-primary/30 cursor-pointer accent-primary"
                />
              </div>
            )}
            <div className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground border-r border-border/20">
              Participant
            </div>
            {vmColumns.map((col) => (
              <div key={col.key} className="px-4 py-2.5 border-r border-border/20 last:border-r-0">
                <EditableColumnHeader column={col} canEdit={canEdit} onRename={onRenameColumn} onDelete={onDeleteColumn} />
              </div>
            ))}
            {canEdit && (
              <div className="px-2 py-2.5 flex items-center justify-center">
                <button onClick={(e) => { e.stopPropagation(); onAddColumn(); }}
                  className="p-0.5 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors" title="Ajouter une colonne">
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Table body */}
          {entries.length === 0 && !addingRow ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground/60">
              <Server className="h-8 w-8 mb-2 opacity-40" />
              <p className="text-sm">Aucune VM pour ce cours</p>
              {canEdit && (
                <Button variant="ghost" size="sm" className="mt-2 text-xs gap-1.5 text-primary"
                  onClick={() => setAddingRow(true)}>
                  <Plus className="h-3 w-3" />
                  Ajouter un participant
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border/20">
              {/* Existing rows */}
              {entries.map((entry) => {
                const participant = getParticipant(entry.participant_id);
                const isSelected = selectedIds.has(entry.id);
                const isDeleting = deletingIds.has(entry.id);
                return (
                  <div key={entry.id} className={`grid gap-0 group transition-colors duration-150 ${isSelected ? 'bg-primary/[0.04]' : 'hover:bg-muted/20'} ${isDeleting ? 'opacity-50' : ''}`}
                    style={{ gridTemplateColumns: gridCols }}>
                    {/* Checkbox cell */}
                    {canEdit && (
                      <div className="px-2 py-2.5 flex items-center justify-center border-r border-border/10">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(entry.id)}
                          className="h-3.5 w-3.5 rounded border-border/50 text-primary focus:ring-primary/30 cursor-pointer accent-primary"
                        />
                      </div>
                    )}
                    {/* Participant cell (read-only) */}
                    <div className="px-3 py-2.5 border-r border-border/10 flex items-center gap-2.5 min-w-0">
                      {participant ? (
                        <>
                          <Avatar className="h-7 w-7 ring-1 ring-border/30 shrink-0">
                            <AvatarFallback className="bg-gradient-to-br from-violet-500/20 to-purple-600/20 text-primary text-[10px] font-semibold">
                              {getInitials(participant.first_name, participant.last_name)}
                            </AvatarFallback>
                          </Avatar>
                          <p className="text-sm font-medium truncate">{participant.first_name} {participant.last_name}</p>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">Participant inconnu</span>
                      )}
                    </div>
                    {/* Editable cells */}
                    {vmColumns.map((col) => (
                      <div key={col.key} className="px-3 py-2 border-r border-border/10 last:border-r-0 flex items-center min-w-0">
                        {canEdit ? (
                          <EditableCell
                            value={getCellValue(entry, col)}
                            placeholder={col.placeholder}
                            isPassword={col.is_password}
                            onBlur={(val) => handleCellUpdate(entry.id, col, val)}
                          />
                        ) : col.is_password ? (
                          <ReadOnlyPasswordCell value={getCellValue(entry, col)} />
                        ) : (
                          <span className="text-sm truncate">{getCellValue(entry, col) || "\u2014"}</span>
                        )}
                      </div>
                    ))}
                    {/* Delete icon per row */}
                    {canEdit && (
                      <div className="px-1 py-2 flex items-center justify-center">
                        <button onClick={() => handleSingleDelete(entry.id)}
                          disabled={isDeleting}
                          className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive/30 hover:text-destructive transition-all duration-200"
                          title="Supprimer">
                          {isDeleting ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* New row with inline participant dropdown */}
              {addingRow && (
                <div className="grid gap-0 bg-primary/[0.03] border-t-2 border-primary/20 animate-in fade-in slide-in-from-top-1 duration-200"
                  style={{ gridTemplateColumns: gridCols }}>
                  {/* Empty checkbox cell */}
                  {canEdit && <div className="px-2 py-2.5 border-r border-border/10" />}
                  {/* Participant dropdown cell */}
                  <div className="px-3 py-2.5 border-r border-border/10 min-w-0">
                    {savingId ? (
                      <div className="flex items-center gap-2 py-1">
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        <span className="text-xs text-muted-foreground">Ajout en cours...</span>
                      </div>
                    ) : (
                      <InlineParticipantSelect
                        participants={participants}
                        existingIds={existingParticipantIds}
                        onSelect={handleSelectParticipant}
                        onCancel={() => setAddingRow(false)}
                      />
                    )}
                  </div>
                  {/* Empty editable cells (placeholder) */}
                  {vmColumns.map((col) => (
                    <div key={col.key} className="px-3 py-2.5 border-r border-border/10 last:border-r-0 flex items-center">
                      <span className="text-muted-foreground/30 italic text-xs">{col.placeholder || col.label}</span>
                    </div>
                  ))}
                  {/* Cancel button */}
                  <div className="px-1 py-2.5 flex items-center justify-center">
                    <button onClick={() => setAddingRow(false)}
                      className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Annuler">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Nouvelle ligne shortcut */}
          {canEdit && entries.length > 0 && !addingRow && (
            <div className="px-4 py-2 border-t border-dashed border-border/30">
              <button onClick={() => setAddingRow(true)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors py-1"
                style={{ marginLeft: '36px' }}>
                <Plus className="h-3 w-3" />
                Nouvelle ligne
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Read-only password cell for candidates
function ReadOnlyPasswordCell({ value }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!value) return <span className="text-sm text-muted-foreground">—</span>;

  return (
    <div className="flex items-center gap-1 group">
      <span className="text-sm truncate flex-1 min-w-0">
        {show ? value : "••••••••"}
      </span>
      <button onClick={() => setShow(!show)}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted">
        {show ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
      </button>
      <button onClick={handleCopy}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted">
        {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3 text-muted-foreground" />}
      </button>
    </div>
  );
}

export default function VMsPage() {
  const { workspaceId } = useParams();
  const { canManage, isAdmin, isCandidate, user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [vmEntries, setVmEntries] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [vmColumns, setVmColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [coursesRes, vmsRes, participantsRes, columnsRes] = await Promise.all([
        courseAPI.list(workspaceId),
        vmAPI.list(workspaceId).catch(e => { console.warn("VM list error:", e); return { data: [] }; }),
        workspaceAPI.listParticipants(workspaceId),
        vmColumnsAPI.list(workspaceId).catch(e => { console.warn("VM columns error:", e); return { data: [] }; }),
      ]);
      
      const coursesData = Array.isArray(coursesRes.data) ? coursesRes.data : [];
      const vmsData = Array.isArray(vmsRes.data) ? vmsRes.data : [];
      const participantsData = Array.isArray(participantsRes.data) ? participantsRes.data : [];
      const columnsData = Array.isArray(columnsRes.data) ? columnsRes.data : [];
      
      console.log("[VMsPage] Loaded:", { courses: coursesData.length, vms: vmsData.length, participants: participantsData.length, columns: columnsData.length });
      
      setCourses(coursesData);
      setVmEntries(vmsData);
      setParticipants(participantsData);
      setVmColumns(columnsData);
    } catch (error) {
      console.error("Failed to load VM data:", error);
      toast.error("Erreur lors du chargement des VMs");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (courseId, participantId) => {
    try {
      const res = await vmAPI.create(workspaceId, { course_id: courseId, participant_id: participantId });
      setVmEntries((prev) => [...prev, res.data]);
      toast.success("VM ajoutée");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Erreur lors de l'ajout");
      throw error;
    }
  };

  const handleUpdateEntry = async (vmId, updateData) => {
    try {
      const res = await vmAPI.update(workspaceId, vmId, updateData);
      setVmEntries((prev) => prev.map((e) => (e.id === vmId ? res.data : e)));
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteEntry = async (vmId) => {
    try {
      await vmAPI.delete(workspaceId, vmId);
      setVmEntries((prev) => prev.filter((e) => e.id !== vmId));
      toast.success("VM supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Column management handlers
  const handleRenameColumn = async (columnId, newLabel) => {
    try {
      const res = await vmColumnsAPI.update(workspaceId, columnId, { label: newLabel });
      setVmColumns((prev) => prev.map((c) => (c.id === columnId ? res.data : c)));
    } catch (error) {
      toast.error("Erreur lors du renommage de la colonne");
    }
  };

  const handleDeleteColumn = async (columnId) => {
    try {
      await vmColumnsAPI.delete(workspaceId, columnId);
      setVmColumns((prev) => prev.filter((c) => c.id !== columnId));
      toast.success("Colonne supprimée");
      // Reload entries to reflect updated custom_fields
      const vmsRes = await vmAPI.list(workspaceId);
      setVmEntries(Array.isArray(vmsRes.data) ? vmsRes.data : []);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Erreur lors de la suppression de la colonne");
    }
  };

  const handleAddColumn = async () => {
    const label = prompt("Nom de la nouvelle colonne :");
    if (!label || !label.trim()) return;
    try {
      const res = await vmColumnsAPI.create(workspaceId, { label: label.trim(), placeholder: "", is_password: false });
      setVmColumns((prev) => [...prev, res.data]);
      toast.success(`Colonne "${label.trim()}" ajoutée`);
    } catch (error) {
      toast.error("Erreur lors de l'ajout de la colonne");
    }
  };

  const getEntriesForCourse = (courseId) => vmEntries.filter((e) => e.course_id === courseId);

  // For candidates: only show courses that have VM entries for them
  const visibleCourses = isCandidate
    ? courses.filter((c) => getEntriesForCourse(c.id).length > 0)
    : courses;

  const filteredCourses = visibleCourses.filter((c) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const courseEntries = getEntriesForCourse(c.id);
    return (
      c.title.toLowerCase().includes(term) ||
      courseEntries.some((e) => {
        const p = participants.find((pp) => pp.id === e.participant_id);
        return (
          (p && `${p.first_name} ${p.last_name}`.toLowerCase().includes(term)) ||
          (e.ip_vm || "").toLowerCase().includes(term) ||
          (e.login || "").toLowerCase().includes(term)
        );
      })
    );
  });

  const totalVMs = vmEntries.length;

  // Generate HTML export
  const generateHTML = () => {
    const getParticipant = (pid) => participants.find((p) => p.id === pid);
    const getCellValue = (entry, col) => {
      if (col.is_default) return entry[col.key] || "";
      return (entry.custom_fields || {})[col.key] || "";
    };

    const coursesHtml = visibleCourses.map((course) => {
      const courseEntries = vmEntries.filter((e) => e.course_id === course.id);
      if (courseEntries.length === 0) return "";
      const headerCells = vmColumns.map((col) => `<th>${col.label}</th>`).join("");
      const rows = courseEntries.map((entry) => {
        const p = getParticipant(entry.participant_id);
        const name = p ? `${p.first_name} ${p.last_name}` : "Inconnu";
        const initials = p ? `${(p.first_name || "")[0] || ""}${(p.last_name || "")[0] || ""}`.toUpperCase() : "??";
        const dataCells = vmColumns.map((col) => {
          const val = getCellValue(entry, col);
          return `<td>${col.is_password ? "\u2022\u2022\u2022\u2022\u2022\u2022" : (val || "\u2014")}</td>`;
        }).join("");
        return `<tr><td class="participant-cell"><span class="avatar">${initials}</span>${name}</td>${dataCells}</tr>`;
      }).join("");
      return `
        <div class="course-block">
          <div class="course-header">
            <h2>${course.title}</h2>
            <span class="badge">${courseEntries.length} VM${courseEntries.length > 1 ? "s" : ""}</span>
          </div>
          <table>
            <thead><tr><th>Participant</th>${headerCells}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    }).filter(Boolean).join("");

    const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Machines Virtuelles - Export</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Inter', system-ui, sans-serif; background: #f8fafc; color: #1e293b; padding: 40px 24px; }
    .container { max-width: 1100px; margin: 0 auto; }
    .page-header { text-align: center; margin-bottom: 48px; }
    .page-header h1 { font-size: 28px; font-weight: 700; background: linear-gradient(135deg, #6366f1, #3b82f6); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
    .page-header p { color: #64748b; font-size: 14px; margin-top: 6px; }
    .page-header .meta { display: flex; justify-content: center; gap: 32px; margin-top: 16px; padding: 12px 0; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }
    .page-header .meta div { text-align: center; }
    .page-header .meta .num { font-size: 22px; font-weight: 700; color: #6366f1; }
    .page-header .meta .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; font-weight: 600; }
    .course-block { background: #fff; border-radius: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04); margin-bottom: 28px; overflow: hidden; border: 1px solid #e2e8f0; }
    .course-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px; background: linear-gradient(135deg, #f8fafc, #f1f5f9); border-bottom: 1px solid #e2e8f0; }
    .course-header h2 { font-size: 16px; font-weight: 600; }
    .badge { background: #6366f1; color: #fff; font-size: 11px; font-weight: 600; padding: 4px 12px; border-radius: 20px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #f8fafc; text-transform: uppercase; font-size: 11px; font-weight: 600; letter-spacing: 0.5px; color: #64748b; padding: 12px 16px; text-align: left; border-bottom: 1px solid #e2e8f0; }
    td { padding: 14px 16px; font-size: 14px; border-bottom: 1px solid #f1f5f9; color: #334155; }
    tr:last-child td { border-bottom: none; }
    tr:hover td { background: #fafbff; }
    .participant-cell { display: flex; align-items: center; gap: 10px; font-weight: 500; }
    .avatar { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 10px; background: linear-gradient(135deg, #ede9fe, #e0e7ff); color: #6366f1; font-size: 11px; font-weight: 700; flex-shrink: 0; }
    .footer { text-align: center; margin-top: 40px; padding: 16px 0; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 12px; }
    @media print {
      body { background: #fff; padding: 20px; }
      .course-block { box-shadow: none; break-inside: avoid; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="page-header">
      <h1>\uD83D\uDDA5\uFE0F Machines Virtuelles</h1>
      <p>Export g\u00e9n\u00e9r\u00e9 le ${new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
      <div class="meta">
        <div><div class="num">${totalVMs}</div><div class="lbl">VMs Total</div></div>
        <div><div class="num">${participants.length}</div><div class="lbl">Participants</div></div>
        <div><div class="num">${visibleCourses.length}</div><div class="lbl">Cours</div></div>
      </div>
    </div>
    ${coursesHtml}
    <div class="footer">Takapes Learn &mdash; Document g\u00e9n\u00e9r\u00e9 automatiquement</div>
    <div class="no-print" style="text-align:center;margin-top:20px">
      <button onclick="window.print()" style="padding:10px 28px;border:none;background:linear-gradient(135deg,#6366f1,#3b82f6);color:#fff;border-radius:10px;font-weight:600;font-size:14px;cursor:pointer;font-family:Inter,sans-serif">\uD83D\uDDA8\uFE0F Imprimer</button>
    </div>
  </div>
</body>
</html>`;

    // Download as .html file
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `VMs_Export_${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Fichier HTML téléchargé");
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container">
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Chargement des VMs...</p>
            </div>
          </div>
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/20">
              <Monitor className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Machines Virtuelles</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {isCandidate ? "Vos accès VM par cours" : "Gérez les VMs attribuées par cours"}
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <div className="text-right">
              <p className="text-2xl font-bold tabular-nums text-primary">{totalVMs}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{isCandidate ? "Mes VMs" : "VMs Total"}</p>
            </div>
            {!isCandidate && (
              <>
                <div className="h-8 w-px bg-border/50" />
                <div className="text-right">
                  <p className="text-2xl font-bold tabular-nums">{participants.length}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Participants</p>
                </div>
                <div className="h-8 w-px bg-border/50" />
                <div className="text-right">
                  <p className="text-2xl font-bold tabular-nums">{courses.length}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Cours</p>
                </div>
              </>
            )}
            {!isCandidate && (
              <>
                <div className="h-8 w-px bg-border/50" />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50"
                  onClick={generateHTML}
                >
                  <FileCode className="h-3.5 w-3.5" />
                  Générer HTML
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Search - hidden for candidates */}
        {!isCandidate && (
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Rechercher par participant, IP ou cours..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="vm-search-input" />
          </div>
        )}

        {/* Content */}
        {visibleCourses.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Monitor className="h-12 w-12 text-muted-foreground/40" />
              <h3 className="mt-4 font-semibold">{isCandidate ? "Aucune VM attribuée" : "Aucun cours disponible"}</h3>
              <p className="mt-2 text-sm text-muted-foreground text-center max-w-sm">
                {isCandidate
                  ? "Aucune machine virtuelle ne vous a encore été attribuée."
                  : "Créez d'abord des cours dans votre workspace pour pouvoir y associer des VMs."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredCourses.map((course) => (
              <CourseVMTable
                key={course.id}
                course={course}
                entries={getEntriesForCourse(course.id)}
                participants={participants}
                vmColumns={vmColumns}
                onAddEntry={handleAddEntry}
                onUpdateEntry={handleUpdateEntry}
                onDeleteEntry={handleDeleteEntry}
                onRenameColumn={handleRenameColumn}
                onDeleteColumn={handleDeleteColumn}
                onAddColumn={handleAddColumn}
                canEdit={canManage || isAdmin}
              />
            ))}
            {filteredCourses.length === 0 && searchTerm && (
              <div className="flex flex-col items-center py-12 text-muted-foreground">
                <Search className="h-8 w-8 mb-3 opacity-40" />
                <p className="text-sm">Aucun résultat pour « {searchTerm} »</p>
              </div>
            )}
          </div>
        )}
      </div>
    </WorkspaceLayout>
  );
}
